import { Checkbox, Divider, Select } from "antd";
import type { CheckboxChangeEvent } from "antd/es/checkbox";
import { type Dispatch, type SetStateAction, useEffect } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { StandingColumns } from "~pages/Standings/enums/standingColumns";

const alwaysSelectedColumns = [StandingColumns.RowNumber, StandingColumns.Teamname];

const defaultColumns = [
  StandingColumns.Played,
  StandingColumns.Wins,
  StandingColumns.Losses,
  StandingColumns.WinPercentage,
  StandingColumns.Percentage,
  StandingColumns.ScoredFor,
  StandingColumns.ScoredAgainst,
  StandingColumns.Home,
  StandingColumns.Away,
  StandingColumns.Streak,
  StandingColumns.Conf,
  StandingColumns.Div,
  StandingColumns.StandingPoints,
  StandingColumns.GamesBehind,
  StandingColumns.ResultString,
];

const standingColumnsArray = [
  StandingColumns.Byes,
  StandingColumns.Draws,
  StandingColumns.DrawsAway,
  StandingColumns.DrawsHome,
  StandingColumns.ForfeitsGiven,
  StandingColumns.ForfeitsWonBy,
  StandingColumns.HighestScoreAgainst,
  StandingColumns.HighestScoreAgainstAway,
  StandingColumns.HighestScoreAgainstHome,
  StandingColumns.HighestScoreFor,
  StandingColumns.HighestScoreForAway,
  StandingColumns.HighestScoreForHome,
  StandingColumns.Losses,
  StandingColumns.LossesAway,
  StandingColumns.LossesHome,
  StandingColumns.LowestScoreAgainst,
  StandingColumns.LowestScoreAgainstAway,
  StandingColumns.LowestScoreAgainstHome,
  StandingColumns.LowestScoreFor,
  StandingColumns.LowestScoreForAway,
  StandingColumns.LowestScoreForHome,
  StandingColumns.Percentage,
  StandingColumns.PercentageAway,
  StandingColumns.PercentageHome,
  StandingColumns.Played,
  StandingColumns.PlayedAway,
  StandingColumns.PlayedHome,
  StandingColumns.PointDifference,
  StandingColumns.PointDifferenceAway,
  StandingColumns.PointDifferenceHome,
  StandingColumns.ResultString,
  StandingColumns.ScoredAgainst,
  StandingColumns.ScoredAgainstAway,
  StandingColumns.ScoredAgainstHome,
  StandingColumns.ScoredFor,
  StandingColumns.ScoredForAway,
  StandingColumns.ScoredForHome,
  StandingColumns.Streak,
  StandingColumns.StreakAway,
  StandingColumns.StreakHome,
  StandingColumns.Washouts,
  StandingColumns.WinPercentage,
  StandingColumns.WinPercentageAway,
  StandingColumns.WinPercentageHome,
  StandingColumns.Wins,
  StandingColumns.WinsAway,
  StandingColumns.WinsHome,
  StandingColumns.BonusStandingPoints,
  StandingColumns.ByeStandingPoints,
  StandingColumns.GamesBehind,
  StandingColumns.PenaltyStandingPoints,
  StandingColumns.StandingPoints,
  StandingColumns.StandingPointsGiven,
  StandingColumns.StandingPointsAway,
  StandingColumns.StandingPointsHome,
  StandingColumns.TeamDifferential,
  StandingColumns.TeamDifferentialAway,
  StandingColumns.TeamDifferentialHome,
  StandingColumns.Div,
  StandingColumns.Conf,
  StandingColumns.Home,
  StandingColumns.Away,
];

type Props = {
  disabled: boolean;
  columns: string[];
  setColumns: Dispatch<SetStateAction<string[]>>;
};

export const StandingColumnsSelect = ({ columns, setColumns, disabled }: Props) => {
  const { formatMessage } = useIntl();
  const options = [
    ...alwaysSelectedColumns.map((value) => ({
      label: formatMessage({ id: `standings.column.${value}` }),
      value: value,
      disabled: true,
    })),
    ...standingColumnsArray.map((value) => ({
      label: formatMessage({ id: `standings.column.${value}` }),
      value: value,
    })),
  ];

  useEffect(() => {
    setColumns([...alwaysSelectedColumns, ...defaultColumns]);
  }, [setColumns]);

  const handleSelectAllChange = (e: CheckboxChangeEvent) => {
    if (e.target.checked) {
      setColumns(options.map((option) => option.value));
    } else {
      setColumns([...alwaysSelectedColumns, ...defaultColumns]);
    }
  };

  const isAllSelected = columns.length === options.length && columns.length > 0;

  return (
    <div style={{ display: "flex", alignItems: "center" }}>
      <div>
        <FormattedMessage id={"standings.table.selectedColumns"} />
      </div>
      <Select
        mode="multiple"
        style={{ width: "200px", marginLeft: 10 }}
        placeholder="Select columns"
        value={columns}
        onChange={setColumns}
        maxTagCount={1}
        disabled={disabled}
        dropdownRender={(menu) => (
          <>
            {menu}
            <Divider style={{ margin: "8px 0" }} />
            <Checkbox style={{ marginLeft: 8 }} checked={isAllSelected} onChange={handleSelectAllChange}>
              <FormattedMessage id="select.all" />
            </Checkbox>
          </>
        )}
        options={options}
      />
    </div>
  );
};
